import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import FeedbackForm from '../components/FeedbackForm';
import './QaValidatorPage.css';

// Keys must match the backend QaFeedbackCategory enum.
const QA_FEEDBACK_CATEGORIES = ['false_positive', 'missed_error', 'wrong_severity', 'unclear_explanation'];
const FEEDBACK_PAGE = 'app/qa-validator';

// The validator is one implementation, served by the backend; the app shows it
// in a same-origin frame and lends it the signed-in user's token.
export default function QaValidatorPage() {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const frameRef = useRef(null);
  const resizeRef = useRef(null);
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    api.getQaValidatorEmbed(lang).then(
      (page) => !cancelled && setHtml(page),
      (e) => !cancelled && setError(e.response?.data?.detail || e.message),
    );
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    const onMessage = (event) => {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow || event.data?.type !== 'cc-embed-ready') return;
      frame.contentWindow.postMessage({ type: 'cc-auth', token: api.token }, window.location.origin);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => () => resizeRef.current?.disconnect(), []);

  const onFrameLoad = () => {
    const doc = frameRef.current.contentDocument;
    resizeRef.current?.disconnect();
    resizeRef.current = new ResizeObserver(() => setHeight(doc.documentElement.scrollHeight));
    resizeRef.current.observe(doc.body);
  };

  if (error) return <div className="qa-embed-error">{String(error)}</div>;
  if (html === null) return <div className="qa-embed-loading" aria-busy="true" />;
  return (
    <>
      <iframe
        ref={frameRef}
        className="qa-embed-frame"
        title="QA Validator"
        srcDoc={html}
        onLoad={onFrameLoad}
        style={{ height: height || '80vh' }}
      />
      <div className="qa-embed-feedback">
        <FeedbackForm
          categories={QA_FEEDBACK_CATEGORIES}
          categoryPrefix="feedback.qaCategories"
          onSubmit={({ rating, comment, categories }) =>
            api.submitQaFeedback({ rating, comment, categories, page: FEEDBACK_PAGE, lang })
          }
          onVoice={({ rating, categories, blob, language }) =>
            api.submitQaVoiceFeedback({ rating, categories, blob, language, page: FEEDBACK_PAGE })
          }
        />
      </div>
    </>
  );
}
